import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { LuasAset } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { Switch } from "@/components/ui/switch"

const postData = async (newData: LuasAset) => {
  const response = await api.post("/master/luas-aset/store", newData)
  return response.data
}

const patchData = async (newData: LuasAset) => {
  const response = await api.patch("/master/luas-aset/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_ref_aset_luas: number }) => {
  const response = await api({
    url: "/master/luas-aset/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: LuasAset | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<LuasAset>({
    id_ref_aset_luas: selectedRow?.id_ref_aset_luas || 0,
    aset_luas_start: selectedRow?.aset_luas_start || "",
    aset_luas_end: selectedRow?.aset_luas_end || "",
    aset_luas_text: selectedRow?.aset_luas_text || "",
    skor_aset_luas_ortu: selectedRow?.skor_aset_luas_ortu || 0,
    skor_aset_luas_pribadi: selectedRow?.skor_aset_luas_pribadi || 0,
    is_aktif: selectedRow?.is_aktif ?? true,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listLuasAset"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Kategori Luas Aset"
          description={`Apakah anda yakin akan menghapus data "${selectedRow?.aset_luas_text}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Luas Aset" : "Update Data Luas Aset"}
      />
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <Label>Mulai Dari (nilai)</Label>
          <Input
            placeholder="Ketik kategori pembayaran air (angka)..."
            value={form.aset_luas_start}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                aset_luas_start: event.target.value,
                aset_luas_text: `${event.target.value} m2 - ${prev.aset_luas_end}`,
              }))
            }
          />
        </div>
        <div className="flex-1">
          <Label>Hingga (nilai)</Label>
          <Input
            placeholder="Ketik kategori pembayaran air (angka)..."
            value={form.aset_luas_end}
            onChange={(event) => {
              if (event.target.value == "~") {
                setForm((prev) => ({
                  ...prev,
                  aset_luas_end: event.target.value,
                  aset_luas_text: `>= ${prev.aset_luas_start} m2`,
                }))
              } else {
                setForm((prev) => ({
                  ...prev,
                  aset_luas_end: event.target.value,
                  aset_luas_text: `${prev.aset_luas_start} - ${event.target.value} m2`,
                }))
              }
            }}
          />
        </div>
      </div>

      <div>
        <Label>Nilai Luas Aset (text)</Label>
        <Input
          placeholder="Ketik nilai pembayaran air (text)..."
          value={form.aset_luas_text}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              aset_luas_text: event.target.value,
            }))
          }
        />
      </div>

      <div className="flex-1">
        <Label>Skor Luas Aset (milik orang tua)</Label>
        <Input
          type="number"
          placeholder="Ketik skor luas aset (milik orang tua)..."
          value={form.skor_aset_luas_ortu}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              skor_aset_luas_ortu: Number(event.target.value),
            }))
          }
        />
      </div>
      <div className="flex-1">
        <Label>Skor Luas Aset (milik pribadi)</Label>
        <Input
          type="number"
          placeholder="Ketik skor luas aset (milik pribadi)..."
          value={form.skor_aset_luas_pribadi}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              skor_aset_luas_pribadi: Number(event.target.value),
            }))
          }
        />
      </div>

      <div className="flex gap-3">
        <Switch
          id="status"
          checked={form.is_aktif}
          onCheckedChange={() =>
            setForm((prev) => ({
              ...prev,
              is_aktif: !prev.is_aktif,
            }))
          }
        />
        <Label htmlFor="status">Status (Nonaktif/Aktif)</Label>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
